import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { calendarPlatformsReplacedForTheme } from 'fym-shared/src/music24/oneOffPublish';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useQueryParam } from '../../../../utils/hooks/useQueryParam';
import { channelSlot } from '../promoteSchedule';
import { getPromoteMediaDraft, mediaKindOfDraft, PromoteMediaDraft } from '../draftStore';
import { platformsMissingMedia, platformTakesMedia, supportsStories } from '../platformMeta';
import { isSocialPlatform, ScheduledPostFormat, SerializedSocialChannel, SocialPlatform } from '../types';
import { parseDayKey } from '../calendar/calendarDates';
import { composerImageRefusal, composerVideoRefusal } from './composerFormatLine';
import { isComposerXMonthBlocked } from './composerXLimit';

const TIME_KEY = /^([01]\d|2[0-3]):[0-5]\d$/;
/** The hour of a day that the Calendar hands over with no time. */
const DEFAULT_SLOT_TIME = '12:00';

/** The `datetime-local` value of a Calendar handoff (`?date=YYYY-MM-DD&time=HH:mm`), or null. */
export function handoffPickedAt(date: string | null, time: string | null): string | null {
  if (parseDayKey(date) === null) {
    return null;
  }
  return `${date}T${time !== null && TIME_KEY.test(time) ? time : DEFAULT_SLOT_TIME}`;
}

export type ComposerTiming = 'now' | 'best' | 'pick';
/** `master` is the copy every channel starts from. The rest are overrides. */
export type ComposerTab = 'master' | SocialPlatform;

export interface ComposerRelease {
  title: string;
  artistName: string;
  releaseDate?: string;
  smartLinkId?: number;
}

interface UseComposerDraftProps {
  channels: SerializedSocialChannel[];
  items?: CatalogItem[];
}

function hasHandle(channel: SerializedSocialChannel): boolean {
  return channel.handle !== null && channel.handle !== '';
}

/**
 * Everything the Composer holds before anything is created.
 *
 * A channel with no handle cannot carry a post, so it is never selectable. The
 * media comes from a Promote screen handoff, from the media picker (a kit asset or
 * an AI reel of a song) or from an image upload in the picker.
 */
export function useComposerDraft({ channels, items }: UseComposerDraftProps) {
  const [media, setMedia] = useState<PromoteMediaDraft | undefined>(getPromoteMediaDraft);
  const [master, setMaster] = useState('');
  const [overrides, setOverrides] = useState<Partial<Record<SocialPlatform, string>>>({});
  const [tab, setTab] = useState<ComposerTab>('master');
  const [timing, setTiming] = useState<ComposerTiming>('best');
  const [format, setFormat] = useState<ScheduledPostFormat>(() => getPromoteMediaDraft()?.format ?? 'post');
  const [pickedAt, setPickedAt] = useState('');
  const [itemUuid, setItemUuid] = useState<string | undefined>(() => getPromoteMediaDraft()?.catalogItemUuid);

  const usable = useMemo(() => channels.filter(hasHandle), [channels]);
  /** The channels that the artist picked. Null: the artist picked nothing yet. */
  const [selected, setSelected] = useState<SocialPlatform[] | null>(null);
  /** The picked channels that the last media change took off the post, for one neutral note. */
  const [droppedForMedia, setDroppedForMedia] = useState<SocialPlatform[]>([]);
  /** A channel of a Calendar slot that waits until the media lets it take the post. */
  const [pendingPlatform, setPendingPlatform] = useState<SocialPlatform | null>(null);
  /** True when the Composer edits a post that keeps its own media: the media rules do not apply. */
  const [keepsPostMedia, setKeepsPostMedia] = useState(false);

  const mediaKind = media === undefined ? null : mediaKindOfDraft(media);

  /**
   * The channels that cannot take a feed post with this media (owner request 2026-10-09: the
   * media comes first and decides the channels). No media: TikTok, Instagram and YouTube. An
   * image: YouTube. A video: none. A story has its own rule (`blockedByFormat`).
   */
  const mediaThemeId = media?.themeId;
  /** The platforms that take this media only as a one-off publish (a full-song video). */
  const oneOffBlocked = useMemo<SocialPlatform[]>(
    () => calendarPlatformsReplacedForTheme(mediaThemeId).filter(isSocialPlatform),
    [mediaThemeId]
  );
  const mediaBlocked = useMemo<SocialPlatform[]>(() => {
    // A full-song video goes to YouTube, Facebook and Instagram as a one-off publish from its asset
    // card, not as a Calendar post (owner decision 2026-10-09). Shorts keep every platform.
    return usable
      .map((channel) => channel.platform)
      .filter(
        (platform) =>
          oneOffBlocked.includes(platform) ||
          (format === 'post' && !keepsPostMedia && !platformTakesMedia(platform, mediaKind))
      );
  }, [format, keepsPostMedia, mediaKind, oneOffBlocked, usable]);

  /**
   * The X account used its API posts of this month and the post has a time in this month (design
   * x-connector c2): X is not chosen. A later time, or a draft with no time, opens it again.
   */
  const xChannel = useMemo(() => usable.find((channel) => channel.platform === 'x'), [usable]);
  const isXMonthBlocked = useMemo(
    () => isComposerXMonthBlocked(xChannel, timing, pickedAt),
    [pickedAt, timing, xChannel]
  );

  /** The channels that can take this post now. */
  const pickable = useMemo<SocialPlatform[]>(
    () =>
      usable
        .map((channel) => channel.platform)
        .filter((platform) => !mediaBlocked.includes(platform) && !(platform === 'x' && isXMonthBlocked)),
    [isXMonthBlocked, mediaBlocked, usable]
  );

  // A media change takes the picked channels that cannot take the new media off the post, and
  // the panel says so one time.
  useEffect(() => {
    if (selected === null) {
      return;
    }
    const dropped = selected.filter((platform) => mediaBlocked.includes(platform));
    if (dropped.length === 0) {
      return;
    }
    setSelected(selected.filter((platform) => !mediaBlocked.includes(platform)));
    setDroppedForMedia(dropped);
  }, [mediaBlocked, selected]);

  // The channel of a Calendar slot joins the post as soon as the media lets it.
  useEffect(() => {
    if (pendingPlatform === null || mediaBlocked.includes(pendingPlatform)) {
      return;
    }
    setSelected([pendingPlatform]);
    setPendingPlatform(null);
  }, [mediaBlocked, pendingPlatform]);

  // A rollout step hands over its channels with the media (design rollout-plan c1, "Open in
  // Composer"). They are applied once the channels have loaded; the media rules still apply.
  const isStepChannelsApplied = useRef(false);
  useEffect(() => {
    const handed = getPromoteMediaDraft()?.platforms;
    if (isStepChannelsApplied.current || handed === undefined || usable.length === 0) {
      return;
    }
    isStepChannelsApplied.current = true;
    setSelected(pickable.filter((platform) => handed.includes(platform)));
  }, [pickable, usable.length]);

  // A day or a free slot of the Calendar hands over the date and the time (`?date=&time=`).
  // The Composer then schedules at that time.
  const handoffDate = useQueryParam<null>('date', null, null);
  const handoffTime = useQueryParam<null>('time', null, null);
  const isHandoffApplied = useRef(false);
  useEffect(() => {
    if (isHandoffApplied.current) {
      return;
    }
    isHandoffApplied.current = true;
    const picked = handoffPickedAt(handoffDate, handoffTime);
    if (picked !== null) {
      setTiming('pick');
      setPickedAt(picked);
    }
  }, [handoffDate, handoffTime]);

  // A free calendar slot hands over the channel it was offered for. It is
  // applied once the channels have loaded, and only when that one can post.
  const preselect = useQueryParam<null>('platform', null, null);
  const isPreselectApplied = useRef(false);
  useEffect(() => {
    if (isPreselectApplied.current || !isSocialPlatform(preselect) || usable.length === 0) {
      return;
    }
    isPreselectApplied.current = true;
    if (usable.some((channel) => channel.platform === preselect)) {
      // A channel that needs media waits for it; until then the post goes nowhere.
      setSelected([]);
      setPendingPlatform(preselect);
    }
  }, [preselect, usable]);

  // The channels a post can actually go to. Before the first pick, the post goes to every
  // channel that can take it. A channel that cannot take the media is never chosen. A story
  // only reaches the platforms that take one, whatever is selected.
  const chosen = useMemo<SocialPlatform[]>(() => (selected === null ? pickable : selected), [pickable, selected]);
  const platforms = useMemo<SocialPlatform[]>(() => {
    const reachable = isXMonthBlocked ? chosen.filter((platform) => platform !== 'x') : chosen;
    return format === 'story' ? reachable.filter(supportsStories) : reachable;
  }, [chosen, format, isXMonthBlocked]);

  /** The platforms a story cannot go to, so the chips can say why. */
  const blockedByFormat = useMemo<SocialPlatform[]>(
    () => (format === 'story' ? usable.map((channel) => channel.platform).filter((one) => !supportsStories(one)) : []),
    [format, usable]
  );

  /**
   * The chosen platforms a story cannot go to, for the format lines. The design has one format
   * line per selected channel, so a channel that the artist did not pick gets no "Cannot go out" line.
   */
  const chosenBlockedByFormat = useMemo<SocialPlatform[]>(
    () => (format === 'story' ? chosen.filter((one) => !supportsStories(one)) : []),
    [chosen, format]
  );

  /**
   * The selected platforms that cannot take this post, because it has no media or the wrong kind
   * (YouTube takes a video only). A story has its own rule.
   */
  const missingMedia = useMemo<SocialPlatform[]>(
    () =>
      format === 'story' ? [] : platformsMissingMedia(platforms, media === undefined ? null : mediaKindOfDraft(media)),
    [format, media, platforms]
  );

  /**
   * The selected platforms whose image rule refuses the attached image (fym-shared
   * `postImageRules.ts`, the same rule as the server): an Instagram feed image takes 4:5 to 1.91:1.
   * The format line says why, and the post cannot be scheduled.
   */
  const imageRefused = useMemo<SocialPlatform[]>(() => {
    if (media === undefined || mediaKindOfDraft(media) !== 'image') {
      return [];
    }
    const image = {
      kind: 'image' as const,
      width: media.width ?? null,
      height: media.height ?? null,
      durationSeconds: null,
    };
    return platforms.filter((platform) => composerImageRefusal(platform, image, format) !== null);
  }, [format, media, platforms]);

  /**
   * The selected platforms whose video rule refuses the attached video (fym-shared
   * `postVideoRules.ts`, the same rule as the server): TikTok takes at most 64 MB, an Instagram
   * story at most 60 s. Only the facts that came with the video count (an uploaded video has its
   * size, its shape and its length). The format line says why, and the post cannot be scheduled.
   */
  const videoRefused = useMemo<SocialPlatform[]>(() => {
    if (media === undefined || mediaKindOfDraft(media) !== 'video') {
      return [];
    }
    const video = {
      kind: 'video' as const,
      width: media.width ?? null,
      height: media.height ?? null,
      durationSeconds: media.durationSec,
      sizeBytes: media.sizeBytes ?? null,
    };
    return platforms.filter((platform) => composerVideoRefusal(platform, video, format) !== null);
  }, [format, media, platforms]);

  /** Switching to a story drops the platforms that cannot carry one. */
  const changeFormat = useCallback((next: ScheduledPostFormat) => {
    setFormat(next);
    if (next === 'story') {
      setSelected((current) => (current === null ? null : current.filter(supportsStories)));
    }
  }, []);

  const release = useMemo<ComposerRelease | undefined>(() => {
    const picked = items?.find((item) => item.uuid === itemUuid);
    if (picked !== undefined) {
      return {
        title: picked.title,
        artistName: picked.artistName,
        releaseDate: picked.releaseDate ?? undefined,
        smartLinkId: picked.smartLinkId ?? undefined,
      };
    }
    // A file that names its song (a handover from an older screen with no catalog row) gives the
    // caption writer the song; an upload has no title and gives nothing.
    if (media !== undefined && media.title !== '') {
      return {
        title: media.title,
        artistName: media.artistName,
        releaseDate: media.releaseDate,
        smartLinkId: media.smartLinkId,
      };
    }
    return undefined;
  }, [items, itemUuid, media]);

  const captionFor = useCallback(
    (platform: SocialPlatform): string => overrides[platform] ?? master,
    [master, overrides]
  );

  const setOverride = useCallback((platform: SocialPlatform, text: string) => {
    setOverrides((current) => ({ ...current, [platform]: text }));
  }, []);

  const resetOverride = useCallback((platform: SocialPlatform) => {
    setOverrides((current) => {
      const next = { ...current };
      delete next[platform];
      return next;
    });
  }, []);

  const applyOverrides = useCallback((written: Partial<Record<SocialPlatform, string>>) => {
    setOverrides((current) => ({ ...current, ...written }));
  }, []);

  const togglePlatform = useCallback(
    (platform: SocialPlatform) => {
      if (
        (format === 'story' && !supportsStories(platform)) ||
        mediaBlocked.includes(platform) ||
        (platform === 'x' && isXMonthBlocked)
      ) {
        return;
      }
      setDroppedForMedia([]);
      setPendingPlatform(null);
      setSelected((current) => {
        const base = current ?? pickable;
        return base.includes(platform) ? base.filter((one) => one !== platform) : [...base, platform];
      });
    },
    [format, isXMonthBlocked, mediaBlocked, pickable]
  );

  /** Each channel's own hour tomorrow, which is what "Best time" schedules. */
  const bestSlots = useMemo<Partial<Record<SocialPlatform, string>>>(() => {
    const now = new Date();
    const slots: Partial<Record<SocialPlatform, string>> = {};
    platforms.forEach((platform) => {
      const channel = usable.find((one) => one.platform === platform);
      if (channel !== undefined) {
        slots[platform] = channelSlot(channel, now).toISOString();
      }
    });
    return slots;
  }, [platforms, usable]);

  /**
   * Attaches `next` (a picked or uploaded file), else the file that a Promote screen handed over.
   * The release (and so the SmartLink of the post) changes only when the Composer has none yet: a
   * file of another song keeps the release, and `mediaSong` offers "Link to <song> instead". An
   * upload belongs to no song and never changes it.
   */
  const attachMedia = useCallback((next?: PromoteMediaDraft) => {
    const draft = next ?? getPromoteMediaDraft();
    setDroppedForMedia([]);
    setMedia(draft);
    const songOfFile = draft?.catalogItemUuid;
    if (songOfFile !== undefined) {
      setItemUuid((current) => current ?? songOfFile);
    }
    return draft;
  }, []);

  /** The song of the attached file when it is not the release of the post; else undefined. */
  const mediaSong = useMemo<{ uuid: string; title: string } | undefined>(() => {
    const songOfFile = media?.catalogItemUuid;
    if (songOfFile === undefined || itemUuid === undefined || songOfFile === itemUuid) {
      return undefined;
    }
    const item = items?.find((one) => one.uuid === songOfFile);
    return { uuid: songOfFile, title: item?.title ?? media?.title ?? '' };
  }, [itemUuid, items, media]);

  /** "Link to <song> instead": the post links to the song of the attached file. */
  const linkToMediaSong = useCallback(() => {
    if (mediaSong !== undefined) {
      setItemUuid(mediaSong.uuid);
    }
  }, [mediaSong]);

  const detachMedia = useCallback(() => {
    setDroppedForMedia([]);
    setMedia(undefined);
  }, []);

  /** Selects only this channel (the post that the Composer edits). That post keeps its own media. */
  const selectOnly = useCallback((platform: SocialPlatform) => {
    setKeepsPostMedia(true);
    setSelected([platform]);
  }, []);

  return {
    usable,
    platforms,
    blockedByFormat,
    chosenBlockedByFormat,
    missingMedia,
    mediaBlocked,
    oneOffBlocked,
    droppedForMedia,
    imageRefused,
    videoRefused,
    format,
    changeFormat,
    media,
    master,
    setMaster,
    overrides,
    captionFor,
    setOverride,
    resetOverride,
    applyOverrides,
    tab,
    setTab,
    timing,
    setTiming,
    pickedAt,
    setPickedAt,
    itemUuid,
    setItemUuid,
    release,
    bestSlots,
    togglePlatform,
    attachMedia,
    mediaSong,
    linkToMediaSong,
    detachMedia,
    selectOnly,
    xChannel,
    isXMonthBlocked,
  };
}

export type ComposerDraft = ReturnType<typeof useComposerDraft>;
